import { z } from 'zod';

export const LABEL_DISPLAY_MODES = ['number', 'percentage', 'both'] as const;

export type LabelDisplay = (typeof LABEL_DISPLAY_MODES)[number];

export const LABEL_DISPLAY_DEFAULT: LabelDisplay = 'number';

// Optional, not `.default()`: a default makes the field required in the inferred
// output type, which every settings literal is built against.
export const labelDisplaySchema = z.enum(LABEL_DISPLAY_MODES).optional();

export const resolveLabelDisplay = (
  labelDisplay: LabelDisplay | undefined
): LabelDisplay => labelDisplay ?? LABEL_DISPLAY_DEFAULT;

// Coerces a legacy `showAsPercentage`. Returns the SAME REFERENCE when there is
// nothing to migrate — load-bearing, this feeds the gigawidget memos (ENG-560).
export const migrateLegacyLabelDisplay = <TSettings>(
  settings: TSettings
): TSettings => {
  if (
    typeof settings !== 'object' ||
    settings === null ||
    Array.isArray(settings)
  ) {
    return settings;
  }

  const record = settings as Record<string, unknown>;
  if (
    record.labelDisplay !== undefined ||
    record.showAsPercentage === undefined
  ) {
    return settings;
  }

  const { showAsPercentage, ...rest } = record;

  return {
    ...rest,
    labelDisplay:
      showAsPercentage === true ? 'percentage' : LABEL_DISPLAY_DEFAULT,
  } as TSettings;
};

// Object-level: the legacy value lives under a DIFFERENT KEY, so a field-level
// preprocess would only ever see `undefined`.
export const withLegacyLabelDisplay = <TSchema extends z.ZodTypeAny>(
  schema: TSchema
) => z.preprocess((value) => migrateLegacyLabelDisplay(value), schema);

// Resolved labels, not `t` — TFunction does not satisfy `(k: string) => string`.
export const buildLabelDisplayOptions = (
  labels: Record<LabelDisplay, string>
): { label: string; value: LabelDisplay }[] =>
  LABEL_DISPLAY_MODES.map((value) => ({ value, label: labels[value] }));
